import { useLocation, Link } from "react-router-dom";
import { HomeHero } from "@/components/HomeHeroes";
import { LANDING_LINKS } from "@/lib/seoPages";
import { ArrowRight, MapPin, Navigation, Phone, Clock, Ban } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";
import { Reviews } from "@/components/Reviews";
import { BODYSHOP_IMG } from "@/lib/bodyshop";



export default function Home() {
  const { search } = useLocation();
  return (
    <div className="min-h-screen bg-background grain" data-testid="home-page">
      <Seo title="Centro Auto Pontedera — Noleggio, Vendita Auto e Carrozzeria a Pontedera (PI)" description="Noleggio auto, auto in vendita e carrozzeria a Pontedera (PI): verniciatura a forno, riparazione grandine, sinistri e auto sostitutiva. Pagamento al ritiro o online." path="/" />
      <Navbar />
      <HomeHero version={new URLSearchParams(search).get("versione")} />

      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="home-bodyshop-section">
        <Link to="/carrozzeria-pontedera" className="group relative block h-[320px] sm:h-[460px] rounded-3xl overflow-hidden border border-white/10" data-testid="home-bodyshop-banner">
          <img src={BODYSHOP_IMG.hero} alt="Carrozzeria Centro Auto Pontedera" loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 p-6 sm:p-12">
            <h2 className="font-display text-[1.5rem] sm:text-5xl lg:text-7xl font-extrabold uppercase leading-none" data-testid="home-bodyshop-title">Carrozzeria<br /><span className="amber">dal 1968</span></h2>
            <span className="inline-flex items-center gap-2 mt-4 text-sm sm:text-base font-semibold text-white/90 group-hover:text-[#E63946] group-hover:gap-3 transition-all">Scopri la carrozzeria <ArrowRight className="w-5 h-5" /></span>
          </div>
        </Link>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-8" data-testid="conditions-section">
        <div className="font-mono-tech text-xs tracking-[0.3em] uppercase amber mb-3">Orari e condizioni</div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight uppercase mb-8">Informazioni utili</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl bg-[#181A22] border border-white/10 p-8">
            <div className="w-12 h-12 rounded-xl bg-[#E63946]/15 flex items-center justify-center mb-5"><Clock className="w-6 h-6 text-[#E63946]" /></div>
            <h3 className="font-display font-bold text-lg mb-3">Orari di apertura</h3>
            <div className="text-sm text-muted-foreground leading-relaxed space-y-1">
              <p><span className="text-foreground font-semibold">Lunedì – Venerdì:</span> 8:30 – 12:30 / 14:30 – 18:30</p>
              <p><span className="text-foreground font-semibold">Sabato:</span> su appuntamento</p>
              <a href="tel:0587484791" className="inline-flex items-center gap-2 mt-3 text-[#E63946] hover:text-[#FF9E00] transition-colors font-semibold" data-testid="conditions-phone"><Phone className="w-4 h-4" /> 0587 484791</a>
            </div>
          </div>
          <div className="rounded-2xl bg-[#181A22] border border-white/10 p-8">
            <div className="w-12 h-12 rounded-xl bg-[#3B82F6]/15 flex items-center justify-center mb-5"><Ban className="w-6 h-6 text-[#3B82F6]" /></div>
            <h3 className="font-display font-bold text-lg mb-3">Requisiti per il noleggio</h3>
            <ul className="text-sm text-muted-foreground leading-relaxed space-y-2">
              <li className="flex gap-2"><span className="text-[#E63946]">•</span><span><span className="text-foreground font-semibold">Non disponibile per neopatentati</span> (patente da almeno 3 anni)</span></li>
              <li className="flex gap-2"><span className="text-[#E63946]">•</span> Patente di guida valida e carta d'identità</li>
              <li className="flex gap-2"><span className="text-[#E63946]">•</span> Cauzione rimborsabile trattenuta al ritiro</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-8" data-testid="zones-section">
        <div className="font-mono-tech text-xs tracking-[0.3em] uppercase amber mb-3">Zone servite</div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight uppercase mb-5">Noleggio auto a Pontedera e in Valdera</h2>
        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed max-w-3xl">
          Dalla nostra sede in Via Don Primo Mazzolari 34/38 a Pontedera serviamo Ponsacco, Calcinaia, Bientina, Capannoli, Peccioli, Casciana Terme Lari, Santa Maria a Monte, Montopoli, Cascina e Pisa.
          Noleggio a giornata, weekend o settimana, auto sostitutiva mentre la tua auto è in carrozzeria e auto usate in vendita.
        </p>
        <div className="flex flex-wrap gap-2 mt-6">
          {LANDING_LINKS.map(([to, label]) => (
            <Link key={to} to={to} className="text-sm rounded-full border border-white/10 bg-[#181A22] px-4 py-2 hover:border-[#E63946]/50 transition-colors" data-testid={`zone-link-${to.slice(1)}`}>{label}</Link>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-8" data-testid="map-section">
        <div className="font-mono-tech text-xs tracking-[0.3em] uppercase amber mb-3">Dove siamo</div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight uppercase mb-8">La nostra sede a Pontedera</h2>
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 rounded-2xl bg-[#181A22] border border-white/10 p-8 flex flex-col justify-center">
            <div className="flex items-start gap-3 mb-5">
              <MapPin className="w-5 h-5 amber mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold">Centro Auto Pontedera</div>
                <div className="text-sm text-muted-foreground">Via Don Primo Mazzolari 34/38<br />56025 Pontedera (PI)</div>
              </div>
            </div>
            <div className="flex items-center gap-3 mb-6">
              <Phone className="w-5 h-5 amber shrink-0" />
              <a href="tel:0587484791" className="text-sm hover:text-[#E63946] transition-colors" data-testid="map-phone">0587 484791</a>
            </div>
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=Via+Don+Primo+Mazzolari+38,+56025+Pontedera+PI"
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#E63946] hover:bg-[#D62828] text-white rounded-full h-11 px-6 font-semibold transition-colors"
              data-testid="directions-btn"
            >
              <Navigation className="w-4 h-4" /> Come raggiungerci
            </a>
          </div>
          <div className="lg:col-span-2 rounded-2xl overflow-hidden border border-white/10 min-h-[340px]">
            <iframe
              title="Mappa sede Pontedera"
              src="https://www.google.com/maps?q=Via+Don+Primo+Mazzolari+38,+56025+Pontedera+PI&output=embed"
              className="w-full h-full min-h-[340px]"
              style={{ border: 0, filter: "invert(0.9) hue-rotate(180deg)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="map-iframe"
            />
          </div>
        </div>
      </section>

      <Reviews />

      <Footer />
    </div>
  );
}
